import { AuctionHouseSuggestionLink } from '../AuctionHouseSuggestionLink/AuctionHouseSuggestionLink';
import { BodyPrimary, H1 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { getPriceResultsUrl } from '@/utils/urls';
import { getRoundedNumberText } from '@/pages/SearchPageSimplified/utils/getRoundedNumberText';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { NoSearchResultsHeader } from './components/NoSearchResultsHeader/NoSearchResultsHeader';
import { RelatedCategories } from './components/RelatedCategories/RelatedCategories';
import { SearchParams } from '@/types/SaveSearch';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useMemo } from 'react';
import { useSearchCardLoading } from '../../hooks/useSearchCardLoading';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';

type Props = {
    displayLocation: string;
    displaySearchTerm: string;
    hasNoKeywordSearchResults: boolean;
    isArchivedSearch: boolean;
    isCollectionsPage: boolean;
    isLoadingSearchResults: boolean;
    isSEOSearchPage: boolean;
    saveSearchParams: SearchParams;
    searchTerm: string;
    totalFound: number;
    totalSoldItems: number;
};

export const SearchPageHeader = ({
    displayLocation,
    displaySearchTerm,
    hasNoKeywordSearchResults,
    isArchivedSearch,
    isCollectionsPage,
    isLoadingSearchResults,
    isSEOSearchPage,
    saveSearchParams,
    searchTerm,
    totalFound,
    totalSoldItems,
}: Props) => {
    const { formatMessage } = useIntl();

    // Manage card loading state for SearchPageCardGrid
    useSearchCardLoading(isLoadingSearchResults, totalFound);

    const { totalFoundRounded, totalSoldItemsRounded } = useMemo(() => {
        return {
            totalFoundRounded: getRoundedNumberText({ totalFound }),
            totalSoldItemsRounded: getRoundedNumberText({ totalFound: totalSoldItems }),
        };
    }, [totalFound, totalSoldItems]);

    const showNoResultsContent = useMemo(() => {
        return hasNoKeywordSearchResults && !isLoadingSearchResults;
    }, [hasNoKeywordSearchResults, isLoadingSearchResults]);

    if (isCollectionsPage) {
        return <RelatedCategories />;
    }

    if (!Boolean(searchTerm)) {
        return null;
    }

    return (
        <div
            className="pb-md"
            data-testid="search-page-header"
        >
            {!isSEOSearchPage && <AuctionHouseSuggestionLink isLoadingSearchResults={isLoadingSearchResults} />}

            {showNoResultsContent ? (
                <NoSearchResultsHeader
                    displayLocation={displayLocation}
                    displaySearchTerm={displaySearchTerm}
                />
            ) : (
                <>
                    <div
                        className={cn('mb-sm flex flex-wrap items-end gap-x-xs xsMax:flex-col xsMax:items-start', {
                            'mb-xs': isSEOSearchPage,
                        })}
                    >
                        <H1>
                            {formatMessage({ id: 'searchPage.searchResults.title' }, { searchTerm: displaySearchTerm })}
                            {isSEOSearchPage && displayLocation}
                        </H1>

                        {!isSEOSearchPage && (
                            <h2 className="hui-text-body-primary mb-xs">
                                {isArchivedSearch
                                    ? formatMessage(
                                          { id: 'searchPage.searchResults.soldItemsBody' },
                                          { totalSoldItems: totalSoldItemsRounded }
                                      )
                                    : formatMessage({ id: 'searchPage.searchResults.body' }, { totalFoundRounded })}
                            </h2>
                        )}
                    </div>
                    {isSEOSearchPage && (
                        <>
                            <BodyPrimary className="mr-xs whitespace-normal">
                                {formatMessage(
                                    { id: 'searchPage.seoPage.exploreAllResults' },
                                    {
                                        searchTerm: displaySearchTerm,
                                        totalFoundText: (
                                            <span
                                                className="font-bold"
                                                key="totalFoundText"
                                            >
                                                {formatMessage(
                                                    {
                                                        id: 'searchPage.seoPage.exploreAllResultsInternalText',
                                                    },
                                                    { totalFoundRounded }
                                                )}
                                            </span>
                                        ),
                                    }
                                )}
                                {displayLocation ? `${displayLocation}.` : '.'}
                            </BodyPrimary>
                            <Link
                                aria-label="View price results"
                                href={getPriceResultsUrl(searchTerm)}
                            >
                                {formatMessage({ id: 'searchPage.seoPage.viewPriceResults' })}
                            </Link>
                        </>
                    )}
                    {totalFound === 0 && (
                        <div className="mb-sm">
                            <BodyPrimary>{formatMessage({ id: 'searchPage.getNotified.body' })}</BodyPrimary>
                        </div>
                    )}
                </>
            )}

            <div className={cn('w-min', { 'mt-sm': isSEOSearchPage })}>
                <SaveSearchContainer
                    saveSearchParams={saveSearchParams}
                    small
                    tooltipPlacement="bottom"
                />
            </div>
        </div>
    );
};
